import { createFileRoute } from "@tanstack/react-router";
import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { api } from "@/lib/api";
import { langColors } from "@/lib/utils";
import { useMobileLayout } from "@/hooks/use-mobile-layout";
import { useSessionUI } from "@/stores/session-ui";
import { useUserPreferences } from "@/hooks/use-user-preferences";
import { isRepositoryHidden } from "@/lib/repository-visibility";
import { useOrderedRepositories } from "@/hooks/use-ordered-repositories";
import { useSortableListSensors } from "@/hooks/use-sortable-list-sensors";
import { SortableItem, SortableDragHandle } from "@/components/ui/SortableItem";
import { EmptyRepositoryBadge } from "@/components/ui/empty-repository-badge";
import {
  normalizeArrayResponse,
  normalizeGitHubReposRouteResponse,
} from "@/lib/repository-route-response-normalizers";
import { List } from "@phosphor-icons/react";
import { Loader2, ArrowLeft } from "lucide-react";
import { PostRepoDecisionCard } from "@/components/onboarding/PostRepoDecisionCard";
import { NewRepoDropdown, type CreatedRepositoryDto } from "@/components/repositories/NewRepoDropdown";
import { upsertRepositoryInCache } from "@/lib/repository-list-cache";

export const Route = createFileRoute("/_authed/repos")({
  validateSearch: (search: Record<string, unknown>) => ({
    disconnectRepoId: typeof search.disconnectRepoId === "string" ? search.disconnectRepoId : undefined,
  }),
  component: ReposPage,
});

interface GitHubRepo {
  id: number;
  full_name: string;
  name: string;
  /** Empty string means the GitHub repo has no commits yet. */
  default_branch: string;
  clone_url: string;
  private: boolean;
  language: string | null;
  github_installation_id: string;
  source_scope: "org" | "external";
}

interface GitHubReposResponse {
  repos: GitHubRepo[];
  installationErrors: Array<{ scope: "org" | "external"; message: string }>;
}

interface ConnectedRepo {
  id: string;
  provider?: "github" | "gitea" | string;
  githubId: number | null;
  githubFullName: string;
  displayName?: string | null;
  repositoryFullName?: string | null;
  name: string;
  defaultBranch?: string | null;
  language: string | null;
  isPrivate: boolean | null;
  lifecycleStatus: string;
  deleteError?: string | null;
}

interface DisconnectPreviewResponse {
  repository: {
    id: string;
    name: string;
    githubFullName: string;
    displayName?: string;
    provider?: "github" | "gitea" | string;
    /** True when the underlying git repository (Gitea) will be permanently deleted, not just disconnected from Cord. */
    isCordProject?: boolean;
    lifecycleStatus: string;
    ownerUserId: string | null;
  };
  preview: {
    threadCount: number;
    activeThreadCount: number;
    archivedThreadCount: number;
    deletingThreadCount: number;
    deleteFailedThreadCount: number;
    runningSandboxCount: number;
    impactedUserCount: number;
    activeBuildCount: number;
    readyBuildArtifactCount: number;
    envFileCount: number;
    setupConversationCount: number;
    scopedMcpConfigCount: number;
    scopedSkillCount: number;
    deploymentCount: number;
  };
  permissions: {
    canDisconnect: boolean;
  };
}

// --- Language dot color ---

function LangBadge({ language }: { language: string | null }) {
  if (!language) return null;
  const color = langColors[language] || "#71717a";
  return (
    <span className="flex items-center gap-1.5 text-xs text-zinc-500">
      <span
        className="inline-block size-2 rounded-full"
        style={{ backgroundColor: color }}
      />
      {language}
    </span>
  );
}

// --- Main page ---

function ReposPage() {
  const routeSearch = Route.useSearch();
  const isMobileLayout = useMobileLayout();
  const toggleLeftSidebar = useSessionUI((s) => s.toggleLeftSidebar);
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [disconnectingRepoId, setDisconnectingRepoId] = useState<string | null>(
    routeSearch.disconnectRepoId ?? null,
  );
  const [connectScope, setConnectScope] = useState<"org" | "external">("org");
  /**
   * When set, the user just finished connecting a repo on this page. We swap
   * the connect UI for the shared PostRepoDecisionCard so they can choose
   * between "Start coding now" and "Set up dev environment first" — matching
   * the behavior of the onboarding wizard. Cleared when the user clicks
   * "Connect another repo".
   */
  const [justConnectedRepo, setJustConnectedRepo] = useState<{
    id: string;
    name: string;
    defaultBranch: string | null;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { preferences, patchPreferences } = useUserPreferences();
  const [disconnectConfirmationText, setDisconnectConfirmationText] = useState("");

  useEffect(() => {
    if (!routeSearch.disconnectRepoId) return;
    setDisconnectingRepoId(routeSearch.disconnectRepoId);
    setDisconnectConfirmationText("");
  }, [routeSearch.disconnectRepoId]);

  // Fetch GitHub repos
  const { data: githubReposData, isLoading: ghLoading } = useQuery<GitHubReposResponse>({
    queryKey: ["github-repos"],
    queryFn: async () => {
      const res = await api.api.github.repos.$get();
      if (!res.ok) throw new Error("Failed to fetch repos");
      return normalizeGitHubReposRouteResponse<GitHubRepo>({
        value: await res.json(),
      });
    },
  });

  const githubRepos = githubReposData?.repos ?? [];
  const githubInstallationErrors = githubReposData?.installationErrors ?? [];

  // Fetch connected repos
  const { data: connectedRepos } = useQuery<ConnectedRepo[]>({
    queryKey: ["repos"],
    queryFn: async () => {
      const res = await api.api.repos.$get();
      if (!res.ok) return [];
      return normalizeArrayResponse<ConnectedRepo>({
        value: await res.json(),
      });
    },
  });

  const { data: orgPolicy } = useQuery<{
    allowExternalRepos: boolean;
    requireGithubAccountLink: boolean;
    adminOnlyOrgRepoConnect: boolean;
    canManage: boolean;
  }>({
    queryKey: ["org-policy"],
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/settings/org-policy`, { credentials: "include" });
      if (!res.ok) {
        return {
          allowExternalRepos: true,
          requireGithubAccountLink: false,
          adminOnlyOrgRepoConnect: false,
          canManage: false,
        };
      }
      return res.json();
    },
  });

  const allowExternalRepos = orgPolicy?.allowExternalRepos ?? true;
  const canConnectOrgRepos = !(orgPolicy?.adminOnlyOrgRepoConnect) || Boolean(orgPolicy?.canManage);

  const connectedIds = new Set(
    (connectedRepos ?? []).filter((r) => (r.provider ?? "github") === "github" && typeof r.githubId === "number").map((r) => r.githubId),
  );

  const connected = connectedRepos ?? [];
  const visibleConnected = useMemo(
    () => connected.filter((repo) =>
      repo.lifecycleStatus !== "deleting" && !isRepositoryHidden({ preferences, repositoryId: repo.id })
    ),
    [connected, preferences],
  );
  const hiddenConnected = useMemo(
    () => connected.filter((repo) =>
      repo.lifecycleStatus !== "deleting" && isRepositoryHidden({ preferences, repositoryId: repo.id })
    ),
    [connected, preferences],
  );
  const deletingConnected = useMemo(
    () => connected.filter((repo) => repo.lifecycleStatus === "deleting"),
    [connected],
  );

  const { orderedRepos: orderedVisibleConnected, handleReorder: handleReorderConnected } = useOrderedRepositories({
    repositories: visibleConnected,
    preferences,
    patchPreferences,
  });

  const reposDndSensors = useSortableListSensors();

  const handleRepoDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = orderedVisibleConnected.findIndex((r) => r.id === active.id);
      const newIndex = orderedVisibleConnected.findIndex((r) => r.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      handleReorderConnected({ oldIndex, newIndex });
    },
    [orderedVisibleConnected, handleReorderConnected],
  );

  // Available = GitHub repos not yet connected
  const available = (githubRepos ?? []).filter(
    (r) => !connectedIds.has(r.id) && (allowExternalRepos || r.source_scope !== "external"),
  );

  useEffect(() => {
    if (!allowExternalRepos && connectScope === "external") {
      setConnectScope("org");
    }
  }, [allowExternalRepos, connectScope]);

  const filtered = search.trim()
    ? available.filter((r) =>
        r.full_name.toLowerCase().includes(search.toLowerCase()),
      )
    : available;

  // Reset highlight when results change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [search]);

  // Connect mutation
  const connectRepo = useMutation({
    mutationFn: async (repo: GitHubRepo) => {
      const effectiveScope = repo.source_scope === "external"
        ? "external"
        : connectScope;

      if (effectiveScope === "external" && !allowExternalRepos) {
        throw new Error("External repos are disabled by your organization");
      }

      if (effectiveScope === "org" && !canConnectOrgRepos) {
        throw new Error("Only admins can connect org repos");
      }

      const res = await api.api.repos.$post({
        json: {
          githubId: repo.id,
          githubFullName: repo.full_name,
          githubInstallationId: repo.github_installation_id,
          connectScope: effectiveScope,
          name: repo.name,
          defaultBranch: repo.default_branch,
          cloneUrl: repo.clone_url,
          isPrivate: repo.private,
          language: repo.language,
        },
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({ error: "Failed to connect repo" })) as {
          error?: string;
        };
        throw new Error(payload.error || "Failed to connect repo");
      }
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data?.id) {
        upsertRepositoryInCache({ queryClient, repository: data });
      }
      queryClient.invalidateQueries({ queryKey: ["repos"] });
      // The /github-repos list is filtered client-side against connectedIds,
      // but refetching it after a connect keeps the "available to connect"
      // list fresh — useful since the user is likely to connect another repo
      // after the decision card without reloading the page.
      queryClient.invalidateQueries({ queryKey: ["github-repos"] });
      setSearch("");
      setIsOpen(false);
      if (data?.id) {
        // The `name` column in the repos table is NOT NULL, so `data.name`
        // is always present for successful responses. The fallback is a
        // defensive belt-and-suspenders in case of future schema changes.
        const displayName =
          typeof data.name === "string" && data.name.length > 0
            ? data.name
            : "Repository";
        toast.success(`${displayName} connected`);
        setJustConnectedRepo({
          id: data.id,
          name: displayName,
          defaultBranch: data.defaultBranch ?? null,
        });
      }
    },
    onError: (err, repo) => {
      toast.error(err instanceof Error ? err.message : `Failed to connect ${repo.name}`);
    },
  });

  // Disconnect / delete mutation
  const disconnectRepo = useMutation({
    mutationFn: async ({ repoId, repoName, confirmation, isCordProject }: { repoId: string; repoName: string; confirmation: string; isCordProject?: boolean }) => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/repos/${repoId}/disconnect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ confirmation }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null) as { error?: string } | null;
        const fallbackVerb = isCordProject ? "delete project" : "disconnect repo";
        throw new Error(body?.error ?? `Failed to ${fallbackVerb}`);
      }
      await res.json();
      return { isCordProject, repoName };
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["repos"] });
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      queryClient.invalidateQueries({ queryKey: ["sessions", "archived"] });
      setDisconnectingRepoId(null);
      setDisconnectConfirmationText("");
      const name = data.repoName || "Repository";
      toast.success(
        variables.isCordProject
          ? `"${name}" is being deleted`
          : `"${name}" disconnect started`,
        {
          description: variables.isCordProject
            ? "The project, its git repository, threads, and sandboxes are being cleaned up."
            : "Attached threads and sandboxes are being cleaned up.",
        },
      );
    },
    onError: (error, variables) => {
      const name = variables.repoName || "Repository";
      const title = variables.isCordProject
        ? `Failed to delete "${name}"`
        : `Failed to disconnect "${name}"`;
      const detail = error instanceof Error ? error.message : undefined;
      toast.error(title, { description: detail });
    },
  });

  const disconnectPreview = useQuery<DisconnectPreviewResponse | null>({
    queryKey: ["repo-disconnect-preview", disconnectingRepoId],
    queryFn: async () => {
      if (!disconnectingRepoId) return null;
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/repos/${disconnectingRepoId}/disconnect-preview`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null) as { error?: string } | null;
        throw new Error(body?.error ?? "Failed to load disconnect preview");
      }
      return res.json();
    },
    enabled: Boolean(disconnectingRepoId),
  });

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Keyboard nav
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        return;
      }

      if (!isOpen || filtered.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filtered.length - 1 ? prev + 1 : 0,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filtered.length - 1,
        );
      } else if (e.key === "Enter" && highlightedIndex >= 0) {
        e.preventDefault();
        const repo = filtered[highlightedIndex];
        if (repo && !connectRepo.isPending) {
          connectRepo.mutate(repo);
        }
      }
    },
    [isOpen, filtered, highlightedIndex, connectRepo],
  );

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex < 0 || !dropdownRef.current) return;
    const items = dropdownRef.current.querySelectorAll("[data-repo-item]");
    items[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  const hideRepo = useCallback((repoId: string) => {
    patchPreferences({
      repositories: {
        visibility: {
          [repoId]: "hide",
        },
      },
    });
    toast.success("Repository hidden from your sidebar");
  }, [patchPreferences]);

  const unhideRepo = useCallback((repoId: string) => {
    patchPreferences({
      repositories: {
        visibility: {
          [repoId]: "show",
        },
      },
    });
    toast.success("Repository visible again");
  }, [patchPreferences]);

  const handleNewRepoCreated = useCallback((repo: CreatedRepositoryDto) => {
    setJustConnectedRepo({
      id: repo.id,
      name: repo.displayName ?? repo.repositoryFullName ?? repo.name,
      defaultBranch: repo.defaultBranch ?? null,
    });
  }, []);

  // After a successful manual connect, replace the page body with the shared
  // post-repo decision card so users get the same "start coding vs set up
  // environment" choice as the onboarding wizard, instead of being dumped
  // straight into the build-config settings wizard.
  if (justConnectedRepo) {
    return (
      <div
        className="h-full overflow-y-auto"
        style={isMobileLayout ? { paddingTop: "var(--safe-area-top, 0px)" } : undefined}
      >
        <div
          className={isMobileLayout
            ? "mx-auto max-w-2xl px-4 py-6 animate-[fade-in_0.2s_ease-out]"
            : "mx-auto max-w-2xl px-8 py-10 animate-[fade-in_0.2s_ease-out]"}
        >
          <button
            type="button"
            onClick={() => setJustConnectedRepo(null)}
            className="mb-4 inline-flex items-center gap-1.5 text-xs text-zinc-500 transition-colors hover:text-zinc-300"
          >
            <ArrowLeft className="size-3.5" />
            Connect another repo
          </button>
          <PostRepoDecisionCard
            repoId={justConnectedRepo.id}
            repoName={justConnectedRepo.name}
            defaultBranch={justConnectedRepo.defaultBranch}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-full overflow-y-auto"
      style={isMobileLayout ? { paddingTop: "var(--safe-area-top, 0px)", paddingBottom: "var(--safe-area-bottom, 0px)" } : undefined}
    >
      <div
        className={isMobileLayout
          ? "mx-auto max-w-2xl px-4 py-6 animate-[fade-in_0.2s_ease-out]"
          : "mx-auto max-w-2xl px-8 py-10 animate-[fade-in_0.2s_ease-out]"}
      >
      {/* Header */}
      {isMobileLayout ? (
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <button
              type="button"
              onClick={toggleLeftSidebar}
              aria-label="Open menu"
              className="flex size-10 items-center justify-center rounded-full bg-zinc-800/80 text-zinc-400 active:bg-zinc-700/80 active:scale-95 transition-all"
            >
              <List className="size-5" weight="bold" />
            </button>
            <h1 className="text-[20px] font-bold tracking-tight text-zinc-100">
              Repositories
            </h1>
          </div>
          <p className="mt-1 text-[14px] text-zinc-500">
            Connect repos or start a new project to begin coding.
          </p>
        </div>
      ) : (
        <div className="mb-8">
          <h1 className="text-lg font-semibold tracking-tight text-zinc-100">
            Repositories
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Connect your GitHub repos or start a new project to begin coding with your agent.
          </p>
        </div>
      )}

      <div className={isMobileLayout ? "mb-5" : "mb-6"}>
        <NewRepoDropdown
          allowExternalScope={allowExternalRepos}
          allowOrgScope={canConnectOrgRepos}
          onCreated={handleNewRepoCreated}
        />
      </div>

      {/* Search combobox */}
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setConnectScope("org")}
          disabled={!canConnectOrgRepos}
          className={`rounded-md border transition-colors ${isMobileLayout ? "px-3.5 py-2 text-[13px] active:bg-white/[0.08]" : "px-2.5 py-1 text-xs"} ${connectScope === "org"
            ? "border-white/[0.1] bg-white/[0.06] text-zinc-200"
            : "border-white/[0.06] text-zinc-500 hover:border-white/[0.08] hover:text-zinc-300"} ${!canConnectOrgRepos ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          Connect for org
        </button>
        <button
          type="button"
          onClick={() => setConnectScope("external")}
          disabled={!allowExternalRepos}
          className={`rounded-md border transition-colors ${isMobileLayout ? "px-3.5 py-2 text-[13px] active:bg-white/[0.08]" : "px-2.5 py-1 text-xs"} ${connectScope === "external"
            ? "border-white/[0.1] bg-white/[0.06] text-zinc-200"
            : "border-white/[0.06] text-zinc-500 hover:border-white/[0.08] hover:text-zinc-300"} ${!allowExternalRepos ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          Connect for me
        </button>
      </div>
      {/* Installation error banner */}
      {githubInstallationErrors.length > 0 && (
        <div className="mb-3 rounded-lg border border-amber-900/50 bg-amber-950/20 px-3.5 py-2.5 text-xs text-amber-300">
          <span className="font-medium">Some repositories couldn't be loaded.</span>
          {" "}
          {githubInstallationErrors.some((e) => e.scope === "org")
            ? "Your organization's GitHub connection had a temporary issue."
            : "Your personal GitHub connection had a temporary issue."}
          {" "}Refresh the page to try again.
        </div>
      )}

      <div className="relative mb-10">
        {/* Loading state */}
        {ghLoading && (
          <div className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3.5">
            <Loader2 className="size-4 shrink-0 animate-spin text-zinc-400" />
            <div className="flex flex-col gap-0.5">
              <span className="text-sm text-zinc-300">Loading repositories…</span>
              <span className="text-xs text-zinc-500">Fetching your GitHub repos</span>
            </div>
          </div>
        )}

        {/* Search input — only shown once repos are loaded */}
        {!ghLoading && (
          <div className="relative">
            <svg
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <circle
                cx="7"
                cy="7"
                r="5.25"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M11 11L14 14"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <input
              ref={inputRef}
              type="text"
              placeholder={`Search ${available.length} available repos...`}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              onKeyDown={handleKeyDown}
              className={`w-full rounded-xl border border-white/[0.06] bg-white/[0.02] pl-10 pr-4 text-zinc-100 placeholder-zinc-500 outline-none transition-colors focus:border-white/[0.1] focus:bg-white/[0.04] ${isMobileLayout ? "py-3 text-[15px]" : "py-2.5 text-sm"}`}
            />
            {search && (
              <button
                onClick={() => {
                  setSearch("");
                  inputRef.current?.focus();
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M4 4L10 10M10 4L4 10"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Dropdown */}
        {isOpen && !ghLoading && (
          <div
             ref={dropdownRef}
            className={`absolute left-0 right-0 top-full z-40 mt-1.5 overflow-y-auto rounded-xl border border-white/[0.06] bg-zinc-950 shadow-2xl shadow-black/50 animate-[dropdown-in_0.15s_ease-out] ${isMobileLayout ? "max-h-80" : "max-h-72"}`}
          >
            {filtered.length === 0 ? (
              <div className={`px-4 py-6 text-center text-zinc-600 ${isMobileLayout ? "text-[14px]" : "text-sm"}`}>
                {search
                  ? "No matching repos found"
                  : "All your repos are connected"}
              </div>
            ) : (
              filtered.map((repo, i) => (
                <button
                  key={repo.id}
                  data-repo-item
                  onClick={() => connectRepo.mutate(repo)}
                  onMouseEnter={() => setHighlightedIndex(i)}
                  disabled={connectRepo.isPending}
                  className={`group flex w-full items-center gap-3 text-left transition-colors disabled:opacity-50 ${isMobileLayout ? "px-4 py-3.5 active:bg-white/[0.08]" : "px-4 py-2.5"} ${
                    i === highlightedIndex
                      ? "bg-white/[0.06]"
                      : "hover:bg-white/[0.06]"
                  }`}
                >
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md border border-white/[0.06] bg-zinc-950 text-zinc-500 transition-colors group-hover:border-white/[0.08] group-hover:text-zinc-300">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <path d="M2 2.5A2.5 2.5 0 014.5 0h8.75a.75.75 0 01.75.75v12.5a.75.75 0 01-.75.75h-2.5a.75.75 0 110-1.5h1.75v-2h-8a1 1 0 00-.714 1.7.75.75 0 01-1.072 1.05A2.495 2.495 0 012 11.5v-9zm10.5-1h-6a1 1 0 00-1 1v6.708A2.486 2.486 0 017.5 9h5V1.5zM5 12.25v3.25a.25.25 0 00.4.2l1.45-1.087a.25.25 0 01.3 0L8.6 15.7a.25.25 0 00.4-.2v-3.25a.25.25 0 00-.25-.25h-3.5a.25.25 0 00-.25.25z" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`truncate text-zinc-200 group-hover:text-zinc-100 ${isMobileLayout ? "text-[15px]" : "text-sm"}`}>
                      {repo.full_name}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2">
                      <LangBadge language={repo.language} />
                      {repo.private && (
                        <span className={isMobileLayout ? "text-[12px] text-zinc-600" : "text-xs text-zinc-600"}>Private</span>
                      )}
                      {repo.default_branch.length === 0 && <EmptyRepositoryBadge />}
                    </div>
                  </div>
                  {isMobileLayout ? (
                    <span className="shrink-0 rounded-lg border border-white/[0.08] bg-white/[0.06] px-3 py-1.5 text-[13px] text-zinc-400">
                      Connect
                    </span>
                  ) : (
                    <span className="shrink-0 rounded-md border border-white/[0.08] bg-white/[0.06] px-2.5 py-1 text-xs text-zinc-400 opacity-0 transition-opacity group-hover:opacity-100">
                      Connect
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* Connected repos */}
      {orderedVisibleConnected.length > 0 && (
        <div>
          <div className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-zinc-600">
            <span>Connected</span>
            <span className="rounded-full bg-white/[0.06] px-1.5 py-0.5 text-xs tabular-nums text-zinc-500">
              {orderedVisibleConnected.length}
            </span>
          </div>
          {isMobileLayout ? (
          /* Mobile: simple list without drag-and-drop */
          <div className="space-y-px rounded-xl border border-white/[0.06] overflow-hidden">
            {orderedVisibleConnected.map((repo) => (
              <div
                key={repo.id}
                className="bg-white/[0.02] active:bg-zinc-800/60 transition-colors"
              >
                <div className="flex items-center gap-3 px-4 py-4">
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-emerald-950/40 text-emerald-500/70">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M5.5 8.5L7 10L10.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[15px] font-medium text-zinc-200">
                      {repo.name}
                    </div>
                    <div className="mt-1">
                      <LangBadge language={repo.language} />
                    </div>
                    {repo.lifecycleStatus === "delete_failed" && (
                      <div className="mt-1 text-[13px] text-amber-300">
                        Last {(repo.provider ?? "github") === "gitea" ? "delete" : "disconnect"} attempt failed{repo.deleteError ? `: ${repo.deleteError}` : ". You can retry."}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <button
                      onClick={() => hideRepo(repo.id)}
                      disabled={disconnectRepo.isPending}
                      className="rounded-lg px-3 py-2.5 text-[13px] text-zinc-500 active:bg-white/[0.06] active:text-zinc-200 transition-all disabled:opacity-50"
                    >
                      Hide
                    </button>
                    <button
                      onClick={() => {
                        setDisconnectingRepoId(repo.id);
                        setDisconnectConfirmationText("");
                      }}
                      disabled={disconnectRepo.isPending}
                      className="flex items-center gap-1.5 rounded-lg px-3 py-2.5 text-[13px] text-zinc-500 active:bg-red-950/30 active:text-red-300 transition-all disabled:opacity-50"
                    >
                      {disconnectRepo.isPending && disconnectingRepoId === repo.id && (
                        <Loader2 className="size-3.5 animate-spin" />
                      )}
                      {(repo.provider ?? "github") === "gitea"
                        ? (repo.lifecycleStatus === "delete_failed" ? "Retry" : "Delete")
                        : (repo.lifecycleStatus === "delete_failed" ? "Retry" : "Disconnect")
                      }
                    </button>
                  </div>
                </div>
                {disconnectingRepoId === repo.id && (
                  <div className="border-t border-white/[0.06] px-4 py-3 text-[13px] text-zinc-500">
                    {disconnectPreview.isLoading ? (
                      <div>Loading preview…</div>
                    ) : disconnectPreview.error ? (
                      <div className="text-red-300">
                        {disconnectPreview.error instanceof Error
                          ? disconnectPreview.error.message
                          : "Failed to load preview"}
                      </div>
                    ) : disconnectPreview.data ? (() => {
                      const isCordProject = disconnectPreview.data.repository.isCordProject ?? false;
                      const confirmationName = disconnectPreview.data.repository.displayName ?? disconnectPreview.data.repository.githubFullName;
                      const requiresConfirmation = isCordProject
                        ? (disconnectPreview.data.preview.threadCount > 0 || disconnectPreview.data.preview.deploymentCount > 0)
                        : disconnectPreview.data.preview.threadCount > 0;
                      return (
                      <div className="space-y-3">
                        <div className="text-zinc-400">
                          {isCordProject
                            ? "Deleting this project permanently removes its hosted git repository, all threads, sandboxes, builds, deployments, and project settings. This cannot be undone."
                            : "Disconnecting permanently deletes attached threads and shuts down their sandboxes. This cannot be undone."
                          }
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-[13px] text-zinc-500">
                          <div className="rounded-md border border-white/[0.06] px-2 py-1.5">
                            <div className="text-zinc-600">Threads</div>
                            <div className="mt-1 text-[14px] text-zinc-200">{disconnectPreview.data.preview.threadCount}</div>
                          </div>
                          <div className="rounded-md border border-white/[0.06] px-2 py-1.5">
                            <div className="text-zinc-600">Sandboxes</div>
                            <div className="mt-1 text-[14px] text-zinc-200">{disconnectPreview.data.preview.runningSandboxCount}</div>
                          </div>
                        </div>
                        {requiresConfirmation && (
                          <div className="space-y-1.5">
                            <label className="block text-zinc-400">
                              Type <span className="font-mono text-zinc-200">{confirmationName}</span> to confirm
                            </label>
                            <input
                              value={disconnectConfirmationText}
                              onChange={(event) => setDisconnectConfirmationText(event.target.value)}
                              className="w-full rounded-lg border border-white/[0.06] bg-zinc-950/70 px-3 py-2.5 text-[14px] text-zinc-100 outline-none transition-colors focus:border-white/[0.1]"
                            />
                          </div>
                        )}
                        {!disconnectPreview.data.permissions.canDisconnect && (
                          <div className="text-red-300">
                            Only the repo owner or an org admin can {isCordProject ? "delete" : "disconnect"} this {isCordProject ? "project" : "repository"}.
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setDisconnectingRepoId(null);
                              setDisconnectConfirmationText("");
                            }}
                            disabled={disconnectRepo.isPending}
                            className="flex-1 rounded-lg border border-white/[0.06] py-2.5 text-[14px] text-zinc-400 active:bg-white/[0.04] transition-colors disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => disconnectRepo.mutate({
                              repoId: repo.id,
                              repoName: repo.displayName ?? repo.repositoryFullName ?? repo.githubFullName ?? repo.name,
                              confirmation: disconnectConfirmationText,
                              isCordProject,
                            })}
                            disabled={
                              disconnectRepo.isPending ||
                              !disconnectPreview.data.permissions.canDisconnect ||
                              (
                                requiresConfirmation &&
                                disconnectConfirmationText.trim() !== confirmationName
                              )
                            }
                            className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-red-900/70 bg-red-950/40 py-2.5 text-[14px] text-red-300 active:bg-red-950/60 transition-colors disabled:opacity-50"
                          >
                            {disconnectRepo.isPending && <Loader2 className="size-3.5 animate-spin" />}
                            {disconnectRepo.isPending
                              ? (isCordProject ? "Deleting…" : "Disconnecting…")
                              : (isCordProject ? "Delete" : "Disconnect")
                            }
                          </button>
                        </div>
                      </div>
                      );
                    })() : null}
                  </div>
                )}
              </div>
            ))}
          </div>
          ) : (
          /* Desktop: drag-and-drop sortable cards */
          <DndContext
            sensors={reposDndSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleRepoDragEnd}
          >
          <SortableContext
            items={orderedVisibleConnected.map((r) => r.id)}
            strategy={verticalListSortingStrategy}
          >
          <div className="space-y-1.5">
            {orderedVisibleConnected.map((repo) => (
              <SortableItem
                key={repo.id}
                id={repo.id}
                className="group rounded-xl border border-white/[0.06] bg-white/[0.02] transition-all hover:border-white/[0.08] hover:bg-white/[0.04]"
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  <SortableDragHandle
                    className="flex shrink-0 items-center justify-center rounded p-1 transition-opacity cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 pointer-coarse:opacity-40 text-zinc-700 hover:text-zinc-400"
                    iconClassName="size-4"
                  />
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-emerald-950/40 text-emerald-500/70">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <circle
                        cx="8"
                        cy="8"
                        r="6.25"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M5.5 8.5L7 10L10.5 6.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-zinc-200">
                      {repo.name}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="truncate text-xs text-zinc-600">
                      {repo.displayName ?? repo.repositoryFullName ?? repo.githubFullName}
                      </span>
                      <LangBadge language={repo.language} />
                    </div>
                    {repo.lifecycleStatus === "delete_failed" && (
                      <div className="mt-1 text-xs text-amber-300">
                        Last {(repo.provider ?? "github") === "gitea" ? "delete" : "disconnect"} attempt failed{repo.deleteError ? `: ${repo.deleteError}` : ". You can retry."}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => hideRepo(repo.id)}
                    disabled={disconnectRepo.isPending}
                    className="shrink-0 rounded-md border border-transparent px-2.5 py-1 text-xs text-zinc-500 transition-all hover:border-white/[0.06] hover:bg-white/[0.06] hover:text-zinc-200 disabled:opacity-50"
                  >
                    Hide
                  </button>
                  <button
                    onClick={() => {
                      setDisconnectingRepoId(repo.id);
                      setDisconnectConfirmationText("");
                    }}
                    disabled={disconnectRepo.isPending}
                    className="shrink-0 flex items-center gap-1.5 rounded-md border border-transparent px-2.5 py-1 text-xs text-zinc-500 transition-all hover:border-red-900/60 hover:bg-red-950/30 hover:text-red-300 disabled:opacity-50"
                  >
                    {disconnectRepo.isPending && disconnectingRepoId === repo.id && (
                      <Loader2 className="size-3 animate-spin" />
                    )}
                    {(repo.provider ?? "github") === "gitea"
                      ? (repo.lifecycleStatus === "delete_failed" ? "Retry Delete" : "Delete project")
                      : (repo.lifecycleStatus === "delete_failed" ? "Retry Disconnect" : "Disconnect")
                    }
                  </button>
                </div>
                {disconnectingRepoId === repo.id && (
                  <div className="border-t border-white/[0.06] px-4 py-3 text-xs text-zinc-500">
                    {disconnectPreview.isLoading ? (
                      <div>Loading preview…</div>
                    ) : disconnectPreview.error ? (
                      <div className="text-red-300">
                        {disconnectPreview.error instanceof Error
                          ? disconnectPreview.error.message
                          : "Failed to load preview"}
                      </div>
                    ) : disconnectPreview.data ? (() => {
                      const isCordProject = disconnectPreview.data.repository.isCordProject ?? false;
                      const confirmationName = disconnectPreview.data.repository.displayName ?? disconnectPreview.data.repository.githubFullName;
                      // Cord projects require confirmation whenever there's any data to delete
                      // (threads, deployments, env files, etc.), not just threads.
                      const requiresConfirmation = isCordProject
                        ? (disconnectPreview.data.preview.threadCount > 0 || disconnectPreview.data.preview.deploymentCount > 0)
                        : disconnectPreview.data.preview.threadCount > 0;
                      return (
                      <div className="space-y-3">
                        <div className="text-zinc-400">
                          {isCordProject
                            ? "Deleting this project permanently removes its hosted git repository, all threads, sandboxes, builds, deployments, and project settings. This cannot be undone."
                            : "Disconnecting permanently deletes attached threads and shuts down their sandboxes. This cannot be undone."
                          }
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-zinc-500 sm:grid-cols-4">
                          <div className="rounded-md border border-white/[0.06] px-2 py-1.5">
                            <div className="text-zinc-600">Threads</div>
                            <div className="mt-1 text-sm text-zinc-200">{disconnectPreview.data.preview.threadCount}</div>
                          </div>
                          <div className="rounded-md border border-white/[0.06] px-2 py-1.5">
                            <div className="text-zinc-600">Running sandboxes</div>
                            <div className="mt-1 text-sm text-zinc-200">{disconnectPreview.data.preview.runningSandboxCount}</div>
                          </div>
                          <div className="rounded-md border border-white/[0.06] px-2 py-1.5">
                            <div className="text-zinc-600">Active builds</div>
                            <div className="mt-1 text-sm text-zinc-200">{disconnectPreview.data.preview.activeBuildCount}</div>
                          </div>
                          <div className="rounded-md border border-white/[0.06] px-2 py-1.5">
                            <div className="text-zinc-600">Impacted users</div>
                            <div className="mt-1 text-sm text-zinc-200">{disconnectPreview.data.preview.impactedUserCount}</div>
                          </div>
                        </div>
                        <div className="space-y-1 text-zinc-500">
                          <div>Archived threads: {disconnectPreview.data.preview.archivedThreadCount}</div>
                          {disconnectPreview.data.preview.deploymentCount > 0 && (
                            <div>Deployments: {disconnectPreview.data.preview.deploymentCount}</div>
                          )}
                          <div>Env files: {disconnectPreview.data.preview.envFileCount}</div>
                          <div>Setup conversations: {disconnectPreview.data.preview.setupConversationCount}</div>
                          <div>Repo AI tools: {disconnectPreview.data.preview.scopedMcpConfigCount + disconnectPreview.data.preview.scopedSkillCount}</div>
                        </div>
                        {requiresConfirmation && (
                          <div className="space-y-1.5">
                            <label className="block text-zinc-400">
                              Type <span className="font-mono text-zinc-200">{confirmationName}</span> to confirm
                            </label>
                            <input
                              value={disconnectConfirmationText}
                              onChange={(event) => setDisconnectConfirmationText(event.target.value)}
                              className="w-full rounded-md border border-white/[0.06] bg-zinc-950/70 px-3 py-2 text-xs text-zinc-100 outline-none transition-colors focus:border-white/[0.1]"
                            />
                          </div>
                        )}
                        {!disconnectPreview.data.permissions.canDisconnect && (
                          <div className="text-red-300">
                            Only the repo owner or an org admin can {isCordProject ? "delete" : "disconnect"} this {isCordProject ? "project" : "repository"}.
                          </div>
                        )}
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <button
                            onClick={() => hideRepo(repo.id)}
                            disabled={disconnectRepo.isPending}
                            className="rounded-md border border-white/[0.06] px-2.5 py-1 text-xs text-zinc-400 transition-colors hover:border-white/[0.08] hover:text-zinc-200 disabled:opacity-50"
                          >
                            Hide instead
                          </button>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setDisconnectingRepoId(null);
                                setDisconnectConfirmationText("");
                              }}
                              disabled={disconnectRepo.isPending}
                              className="rounded-md border border-white/[0.06] px-2.5 py-1 text-xs text-zinc-400 transition-colors hover:border-white/[0.08] hover:text-zinc-200 disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => disconnectRepo.mutate({
                                repoId: repo.id,
                                repoName: repo.displayName ?? repo.repositoryFullName ?? repo.githubFullName ?? repo.name,
                                confirmation: disconnectConfirmationText,
                                isCordProject,
                              })}
                              disabled={
                                disconnectRepo.isPending ||
                                !disconnectPreview.data.permissions.canDisconnect ||
                                (
                                  requiresConfirmation &&
                                  disconnectConfirmationText.trim() !== confirmationName
                                )
                              }
                              className="flex items-center gap-1.5 rounded-md border border-red-900/70 bg-red-950/40 px-2.5 py-1 text-xs text-red-300 transition-colors hover:border-red-700 hover:bg-red-950/60 disabled:opacity-50"
                            >
                              {disconnectRepo.isPending && <Loader2 className="size-3 animate-spin" />}
                              {disconnectRepo.isPending
                                ? (isCordProject ? "Deleting…" : "Disconnecting…")
                                : (isCordProject ? "Delete permanently" : "Disconnect permanently")
                              }
                            </button>
                          </div>
                        </div>
                      </div>
                      );
                    })() : null}
                  </div>
                )}
              </SortableItem>
            ))}
          </div>
          </SortableContext>
          </DndContext>
          )}
        </div>
      )}

      {hiddenConnected.length > 0 && (
        <div className="mt-8">
          <div className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-zinc-600">
            <span>Hidden</span>
            <span className="rounded-full bg-white/[0.06] px-1.5 py-0.5 text-xs tabular-nums text-zinc-500">
              {hiddenConnected.length}
            </span>
          </div>
          <div className={isMobileLayout ? "space-y-px rounded-xl border border-white/[0.06] overflow-hidden" : "space-y-1.5"}>
            {hiddenConnected.map((repo) => (
              <div
                key={repo.id}
                className={isMobileLayout ? "bg-white/[0.015]" : "rounded-xl border border-white/[0.06] bg-white/[0.015]"}
              >
                <div className={isMobileLayout ? "flex items-center gap-3 px-4 py-4" : "flex items-center gap-3 px-4 py-3"}>
                  <div className={`flex shrink-0 items-center justify-center rounded-md bg-white/[0.02] text-zinc-500 ${isMobileLayout ? "size-9 rounded-lg" : "size-8"}`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M5 8H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={isMobileLayout ? "truncate text-[15px] font-medium text-zinc-300" : "truncate text-sm font-medium text-zinc-300"}>{repo.name}</div>
                    <div className="mt-0.5 flex items-center gap-3">
                      {!isMobileLayout && <span className="truncate text-xs text-zinc-600">{repo.displayName ?? repo.repositoryFullName ?? repo.githubFullName}</span>}
                      <LangBadge language={repo.language} />
                    </div>
                  </div>
                  <button
                    onClick={() => unhideRepo(repo.id)}
                    className={isMobileLayout
                      ? "shrink-0 rounded-lg px-4 py-2.5 text-[14px] text-zinc-400 active:bg-white/[0.06] active:text-zinc-200 transition-colors"
                      : "shrink-0 rounded-md border border-white/[0.06] px-2.5 py-1 text-xs text-zinc-400 transition-colors hover:border-white/[0.08] hover:text-zinc-200"}
                  >
                    Unhide
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {deletingConnected.length > 0 && (
        <div className="mt-8">
          <div className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-zinc-600">
            <span>Cleaning up</span>
            <span className="rounded-full bg-white/[0.06] px-1.5 py-0.5 text-xs tabular-nums text-zinc-500">
              {deletingConnected.length}
            </span>
          </div>
          <div className="space-y-1.5">
            {deletingConnected.map((repo) => {
              const isGitea = (repo.provider ?? "github") === "gitea";
              return (
              <div
                key={repo.id}
                className="rounded-xl border border-amber-900/40 bg-amber-950/10"
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-amber-950/40 text-amber-400/80">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M8 4.5V8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <circle cx="8" cy="11.25" r="0.75" fill="currentColor" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-zinc-200">{repo.name}</div>
                    <div className="mt-0.5 text-xs text-zinc-500">
                      {isGitea
                        ? "Deleting project, its git repository, threads, and sandboxes…"
                        : "Disconnecting and deleting attached threads and sandboxes…"
                      }
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!ghLoading && connected.length === 0 && (
        <div className="rounded-2xl border border-dashed border-white/[0.06] px-6 py-12 text-center">
          <div className="mx-auto mb-3 flex size-10 items-center justify-center rounded-xl bg-white/[0.02] border border-white/[0.06] text-zinc-600">
            <svg
              width="20"
              height="20"
              viewBox="0 0 16 16"
              fill="currentColor"
            >
              <path d="M2 2.5A2.5 2.5 0 014.5 0h8.75a.75.75 0 01.75.75v12.5a.75.75 0 01-.75.75h-2.5a.75.75 0 110-1.5h1.75v-2h-8a1 1 0 00-.714 1.7.75.75 0 01-1.072 1.05A2.495 2.495 0 012 11.5v-9zm10.5-1h-6a1 1 0 00-1 1v6.708A2.486 2.486 0 017.5 9h5V1.5zM5 12.25v3.25a.25.25 0 00.4.2l1.45-1.087a.25.25 0 01.3 0L8.6 15.7a.25.25 0 00.4-.2v-3.25a.25.25 0 00-.25-.25h-3.5a.25.25 0 00-.25.25z" />
            </svg>
          </div>
          <p className="text-sm text-zinc-400">
            No repositories connected yet.
          </p>
          <p className="mt-1 text-xs text-zinc-600">
            Search above to find and connect your GitHub repos.
          </p>
        </div>
      )}
    </div>
    </div>
  );
}
